import React, { useMemo } from 'react';
import { DataGrid, useUnits } from '@innovyze/stylovyze';
import { Component, ComponentType, getTypeDisplay } from '@Types';
import { tableColumns } from './TabelColumns';
import { useGlobalization } from '@Translations/useGlobalization';
import { TableToolbar } from './TableToolbar';
import { fixupTaskField } from '../AddEditComponent';

interface ParameterDetailsProps {
	componentParameter1?: Component;
	componentParameter2?: Component;
}

interface ParameterDetailsRow {
	id: number;
	name: string;
	type: string;
	dataSource: string;
	field: string;
	distance: string;
}

const ParameterDetails = ({
	componentParameter1,
	componentParameter2,
}: ParameterDetailsProps) => {
	const { t } = useGlobalization();
	const { getSystemValueFormatted } = useUnits();

	const getParamDetails = (
		index: number,
		parameter: Component,
	): ParameterDetailsRow => {
		switch (parameter.type) {
			case ComponentType.SpatialProximity: {
				return {
					id: index,
					name: parameter.name,
					type: getTypeDisplay(parameter.type as ComponentType, t),
					dataSource: parameter.layer ?? '',
					field: parameter.fieldAlias ?? '',
					distance: '',
				};
			}

			case ComponentType.SpatialAttribute: {
				let distance = '';
				if (parameter.maxDistance !== undefined) {
					if (parameter.unitMetadata) {
						distance =
							getSystemValueFormatted(
								`${parameter.maxDistance}m`,
							) ?? '';
					} else {
						distance = parameter.maxDistance.toString();
					}
				}

				return {
					id: index,
					name: parameter.name,
					type: getTypeDisplay(parameter.type as ComponentType, t),
					dataSource: parameter.layer ?? '',
					field: parameter.field ?? '',
					distance: distance,
				};
			}
			case ComponentType.CustomTable: {
				return {
					id: index,
					name: parameter.name,
					type: getTypeDisplay(parameter.type as ComponentType, t),
					dataSource: parameter.layer ?? '',
					field: parameter.fieldAlias ?? '',
					distance: '',
				};
			}
			case ComponentType.Task: {
				return {
					id: index,
					name: parameter.name,
					type: getTypeDisplay(parameter.type as ComponentType, t),
					dataSource: parameter.taskName ?? '',
					field: fixupTaskField(t, parameter.fieldAlias ?? ''),
					distance: '',
				};
			}
			case ComponentType.Simulation: {
				return {
					id: index,
					name: parameter.name,
					type: getTypeDisplay(parameter.type as ComponentType, t),
					dataSource: parameter.simulationName ?? '',
					field: parameter.fieldAlias ?? '',
					distance: '',
				};
			}
			default: {
				return {
					id: index,
					name: parameter.name,
					type: getTypeDisplay(parameter.type as ComponentType, t),
					dataSource: '',
					field: parameter.fieldAlias ?? '',
					distance: '',
				};
			}
		}
	};

	const rows = useMemo((): ParameterDetailsRow[] => {
		const rows: ParameterDetailsRow[] = Array<ParameterDetailsRow>();

		if (
			componentParameter1 !== undefined &&
			componentParameter1.name !== ''
		) {
			rows.push(getParamDetails(1, componentParameter1));
		}
		if (
			componentParameter2 !== undefined &&
			componentParameter2.name !== ''
		) {
			rows.push(getParamDetails(2, componentParameter2));
		}

		return rows;
	}, [componentParameter1, componentParameter2]);

	if (
		componentParameter1 &&
		componentParameter1.metadata &&
		componentParameter2 &&
		componentParameter2.metadata
	) {
		return (
			<DataGrid
				experimentalFeatures={{ columnGrouping: true }}
				disableColumnMenu
				disableColumnReorder
				disableRowSelectionOnClick
				columns={tableColumns()}
				rows={rows}
				slots={{
					toolbar: TableToolbar,
				}}
			/>
		);
	} else {
		return <></>;
	}
};

export default ParameterDetails;
